import { useSendContactInfo } from "@/api/free-assessment/useSendContactInfo";
import { Button } from "@/components/ui/button";
import { useTurnstileToken } from "@/hooks/useTurnstileToken";
import {
  calculateAssessmentResult,
  getAssessmentPaperId,
  getAssessmentResult,
  getUserInfo,
} from "@/lib/freeAssessment";
import toast from "@/lib/toast";
import { Category as TCategory } from "@/schema/free-assessment/Category";
import Category from "@/src/free-assessment/questions/_components/Category/Category";
import Heading from "@/src/free-assessment/questions/_components/Heading/Heading";
import WarningDialog from "@/src/free-assessment/questions/_components/WarningDialog";
import { Fragment, useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface AssessmentFormProps {
  categories: TCategory[];
}

function AssessmentForm({ categories }: AssessmentFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // don't set open true when first render to avoid hydration error
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setOpen(true);
  }, []);

  const categoryCount = categories.length;
  const questionCount = categories.reduce(
    (acc, category) =>
      acc +
      category.sub_categories.reduce(
        (acc, subCategory) => acc + subCategory.questions.length,
        0,
      ),
    0,
  );

  const { control, handleSubmit } = useForm<{ categories: TCategory[] }>({
    defaultValues: {
      categories,
    },
    shouldFocusError: true,
  });
  const { fields } = useFieldArray({
    control,
    name: "categories",
  });
  const info = getUserInfo();
  const assessmentPaperId = getAssessmentPaperId();
  const {
    ref: turnstileRef,
    token: turnstileToken,
    isReady: isTurnstileReady,
    isLoading: isTurnstileLoading,
    error: turnstileError,
    reset: resetTurnstile,
  } = useTurnstileToken();
  const { trigger, isMutating } = useSendContactInfo({
    onSuccess: () => {
      navigate("/free-assessment/result");
    },
  });

  return (
    <form
      onSubmit={handleSubmit(async (data) => {
        calculateAssessmentResult(data.categories);
        const result = getAssessmentResult();
        if (result) {
          const { total, categories } = result;
          const freeCategoryResults = categories.map((category) => {
            const { id, points, subCategories } = category;
            const freeSubCategoryResults = subCategories.map((subCategory) => {
              const { id, points } = subCategory;
              return {
                sub_category_id: id,
                score: typeof points === "number" ? points : 0,
              };
            });
            return {
              main_category_id: id,
              score: typeof points === "number" ? points : 0,
              free_sub_category_results: freeSubCategoryResults,
            };
          });
          if (!turnstileToken) {
            toast.error(t("turnstile-verification-failed"));
            return;
          }

          try {
            await trigger({
              cf_turnstile_response: turnstileToken,
              assessment_paper_id: assessmentPaperId ?? 0, // Ensure it's a number
              client_name: `${info?.firstName ?? ""} ${info?.lastName ?? ""}`, // Ensure it's a string
              company_name: info?.companyName ?? "", // Ensure it's a string
              referer_id: info?.refererId ?? "", // Ensure it's a string
              email: info?.email ?? "", // Ensure it's a string
              phone: "0000", // Ensure it's a string
              has_inquiry_request: false,
              score: typeof total === "number" ? total : 0,
              free_category_results: freeCategoryResults,
            });
          } finally {
            resetTurnstile();
          }
        }
      })}
    >
      <WarningDialog open={open} onOpenChange={setOpen} />
      <Heading
        isLoading={isMutating}
        categoryCount={categoryCount}
        questionCount={questionCount}
      />
      <div className="pb-8">
        {fields.map((category, index) => (
          <Fragment key={category.id}>
            <Category
              control={control}
              categoryIndex={index}
              category={category}
            />
          </Fragment>
        ))}
      </div>
      <div className="mx-auto mt-4 flex flex-col items-center pb-16">
        <div className="cf-turnstile-container">
          <div ref={turnstileRef} className="cf-turnstile" />
        </div>
        {turnstileError && (
          <p className="mt-2 text-center text-sm text-red-600">
            {t("turnstile-verification-failed")}
          </p>
        )}
        <Button
          className="mt-4"
          isLoading={isMutating}
          type="submit"
          disabled={
            isMutating || !turnstileToken || !isTurnstileReady || isTurnstileLoading
          }
        >
          {t("submit-free-assessment")}
        </Button>
      </div>
    </form>
  );
}

export default AssessmentForm;
